import {all, call, fork, put, takeEvery} from 'bukku/shared/SagaService';
import actions from './actions';
import AppActions from '../App/actions';
import {deleto, get, patch, post, puto} from 'bukku/shared/ApiService';
import {errorSagaFn, successSagaFn} from "../helper";
import {t} from "@lingui/macro";
import { getBundleItemDetail } from './getBundleItemDetail';
import { getBundleItemsDetail } from './getBundleItemsDetail';

const successKeys = [
    actions.BUNDLE_CREATE_SUCCESS,
    actions.BUNDLE_READ_SUCCESS,
    actions.BUNDLE_UPDATE_SUCCESS,
    actions.BUNDLE_ARCHIVE_SUCCESS,
    actions.BUNDLE_DELETE_SUCCESS,
];
const errorKeys = [
    actions.BUNDLE_CREATE_ERROR,
    actions.BUNDLE_READ_ERROR,
    actions.BUNDLE_UPDATE_ERROR,
    actions.BUNDLE_ARCHIVE_ERROR,
    actions.BUNDLE_DELETE_ERROR,
];

const getSuccessMsg = action => {
    switch (action.type) {
        case actions.BUNDLE_CREATE_SUCCESS:
            return t`The bundle is created.`;
        case actions.BUNDLE_UPDATE_SUCCESS:
            return t`The bundle is updated.`;
        case actions.BUNDLE_DELETE_SUCCESS:
            return t`The bundle is deleted.`;
        case actions.BUNDLE_ARCHIVE_SUCCESS: {
            const { bundle } = action;
            return bundle.is_archived ? t`The bundle is deactivated.` : t`The bundle is activated.`;
        }
        default:
            return null;
    }
};
// Create bundle
export function* createBundleRequest() {
    yield takeEvery(actions.BUNDLE_CREATE_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(post, '/products/bundles', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.BUNDLE_CREATE_SUCCESS,
                bundle: data,
            });
        }
        catch (error) {
            yield put({
                type: actions.BUNDLE_CREATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Read bundle
export function* readBundleRequest() {
    yield takeEvery(actions.BUNDLE_READ_REQUEST, function* ({ id }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, `/products/bundles/${id}`, null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.BUNDLE_READ_SUCCESS,
                bundle: data,
            });
        }
        catch (error) {
            yield put({
                type: actions.BUNDLE_READ_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Update bundle
export function* updateBundleRequest() {
    yield takeEvery(actions.BUNDLE_UPDATE_REQUEST, function* ({ id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(puto, `/products/bundles/${id}`, {...request, id}, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.BUNDLE_UPDATE_SUCCESS,
                bundle: data,
            });
        }
        catch (error) {
            yield put({
                type: actions.BUNDLE_UPDATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Archive bundle
export function* archiveBundleRequest() {
    yield takeEvery(actions.BUNDLE_ARCHIVE_REQUEST, function* ({ id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(patch, `/products/bundles/${id}`, request, context);
            const data = yield response.data;

            yield put({
                type: actions.BUNDLE_ARCHIVE_SUCCESS,
                bundle: data,
            });
        }
        catch (error) {
            yield put({
                type: actions.BUNDLE_ARCHIVE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Delete bundle
export function* deleteBundleRequest() {
    yield takeEvery(actions.BUNDLE_DELETE_REQUEST, function* ({ id }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            yield call(deleto, `/products/bundles/${id}`, context, context);

            yield put({
                type: actions.BUNDLE_DELETE_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.BUNDLE_DELETE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Read one item's price
export function* readItemPriceRequest() {
    yield takeEvery(actions.BUNDLE_READ_ITEM_PRICE_REQUEST, function* ({productId, key, unitId}) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });

            const itemWithPrice = yield getBundleItemDetail(productId, key, unitId);

            yield put({
                type: actions.BUNDLE_READ_ITEM_PRICE_SUCCESS,
                itemWithPrice,
            });
        }
        catch (error) {
            yield put({
                type: actions.BUNDLE_READ_ITEM_PRICE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Read all items price
export function* readItemsPriceRequest() {
    yield takeEvery(actions.BUNDLE_READ_ITEMS_PRICE_REQUEST, function* () {
        try {
            yield put({ type: AppActions.APP_START_PULLING });

            const bundleWithPrice = yield getBundleItemsDetail();

            yield put({
                type: actions.BUNDLE_READ_ITEMS_PRICE_SUCCESS,
                bundleWithPrice,
            });
        }
        catch (error) {
            yield put({
                type: actions.BUNDLE_READ_ITEMS_PRICE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

export default function* rootSaga() {
    yield all([
        fork(successSagaFn(successKeys, getSuccessMsg)),
        fork(errorSagaFn(errorKeys)),
        fork(createBundleRequest),
        fork(readBundleRequest),
        fork(updateBundleRequest),
        fork(archiveBundleRequest),
        fork(deleteBundleRequest),
        fork(readItemPriceRequest),
        fork(readItemsPriceRequest),
    ]);
}
